import React from 'react';

interface ErrorFallbackProps {
  error: Error;
  errorInfo?: React.ErrorInfo;
  reset?: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, errorInfo, reset }) => {
  const isDevelopment = process.env.NODE_ENV === 'development';

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8">
        {/* Error Icon */}
        <div className="text-red-600 mb-6 flex justify-center">
          <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        {/* Error Title */}
        <h1 className="text-2xl font-bold text-gray-900 text-center mb-4">
          Oops! Something went wrong
        </h1>

        {/* Error Description */}
        <p className="text-gray-600 text-center mb-6">
          We're sorry, but something unexpected happened. Our team has been notified and we're working on a fix.
        </p>

        {/* Error Details (Development Only) */}
        {isDevelopment && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-red-900 mb-2">Error Details (Development Mode)</h3>
            <div className="text-sm text-red-800 font-mono mb-2">
              <strong>Message:</strong> {error.message}
            </div>
            {error.stack && (
              <details className="text-xs text-red-700">
                <summary className="cursor-pointer font-semibold mb-1">Stack Trace</summary>
                <pre className="whitespace-pre-wrap overflow-auto max-h-64 bg-red-100 p-2 rounded">
                  {error.stack}
                </pre>
              </details>
            )}
            {errorInfo?.componentStack && (
              <details className="text-xs text-red-700 mt-2">
                <summary className="cursor-pointer font-semibold mb-1">Component Stack</summary>
                <pre className="whitespace-pre-wrap overflow-auto max-h-64 bg-red-100 p-2 rounded">
                  {errorInfo.componentStack}
                </pre>
              </details>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {reset && (
            <button
              onClick={reset}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium min-h-[44px]"
            >
              Try Again
            </button>
          )}
          <button
            onClick={handleReload}
            className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition font-medium min-h-[44px]"
          >
            Reload Page
          </button>
          <button
            onClick={handleGoHome}
            className="px-6 py-3 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium min-h-[44px]"
          >
            Go to Home
          </button>
        </div>

        {/* Help Text */}
        <p className="text-sm text-gray-500 text-center mt-6">
          If this problem persists, please contact support with the error details above.
        </p>
      </div>
    </div>
  );
};

export default ErrorFallback;

